/**
 * Minimal storage contract (a subset of the Web Storage API) — the same shape
 * ProgressService and LocalScoreService accept.
 */
export interface StorageLike {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
  removeItem(k: string): void;
}

/** A removed key, remembered so a read does not fall back through to `base`. */
const TOMBSTONE = Symbol('removed');

/**
 * Copy-on-write view over `base`: reads fall through to the real save, writes
 * are captured in memory and never reach it.
 *
 * Used for `?debug` sessions (see `saveStorage()` in `src/render/saveStorage.ts`).
 * A debug session starts from your actual progress — so screens look the way
 * they really look — but anything it does to that progress, whether the
 * PHANTOM UNLOCK / UNLOCK CH2 buttons, a level won on the way through, coins
 * spent in the Shop or a save wiped with Reset, dies with the page. Without
 * this, every debug action wrote straight to `hambi.*` in localStorage and
 * showed up in normal play.
 *
 * Writes are per-key, so a key written here shadows `base` for reads while a
 * key never touched still tracks `base` live.
 */
export function createSandboxStorage(base: StorageLike): StorageLike {
  const overlay = new Map<string, string | typeof TOMBSTONE>();
  return {
    getItem(k) {
      const v = overlay.get(k);
      if (v === TOMBSTONE) return null;
      return v ?? base.getItem(k);
    },
    setItem(k, v) {
      overlay.set(k, v);
    },
    removeItem(k) {
      overlay.set(k, TOMBSTONE);
    },
  };
}
